import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent, COOKIE_CATEGORIES } from '../context/CookieContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Cookie, Shield, BarChart3, Settings, ChevronDown, ChevronUp } from 'lucide-react';

export function CookieBanner() {
  const { showBanner, consent, acceptAll, acceptNecessary, saveConsent } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [customConsent, setCustomConsent] = useState({
    [COOKIE_CATEGORIES.necessary]: true,
    [COOKIE_CATEGORIES.analytics]: consent?.[COOKIE_CATEGORIES.analytics] ?? false
  });

  if (!showBanner) return null;

  const handleCustomSave = () => {
    saveConsent({
      ...customConsent,
      [COOKIE_CATEGORIES.necessary]: true // Always required
    });
  };

  const toggleCategory = (category) => {
    if (category === COOKIE_CATEGORIES.necessary) return; // Can't toggle necessary
    setCustomConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="cookie-banner">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border-2">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/10">
                <Cookie className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Cookie-Einstellungen</CardTitle>
                <CardDescription>Wir respektieren deine Privatsphäre</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Wir nutzen Cookies, um dir die bestmögliche Erfahrung auf unserer Website zu bieten. 
            Einige Cookies sind für den Betrieb der Website notwendig, während andere uns helfen, 
            die Website zu verbessern.
          </p>

          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={acceptAll} 
              className="flex-1"
              data-testid="cookie-accept-all"
            >
              Alle akzeptieren
            </Button>
            <Button 
              onClick={acceptNecessary} 
              variant="outline" 
              className="flex-1"
              data-testid="cookie-accept-necessary"
            >
              Nur notwendige
            </Button>
            <Button
              onClick={() => setShowDetails(!showDetails)}
              variant="ghost"
              className="flex items-center gap-2"
              data-testid="cookie-settings-toggle"
            >
              <Settings className="h-4 w-4" />
              Einstellungen
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>

          {/* Detailed Settings */}
          {showDetails && (
            <div className="space-y-3 pt-4 border-t">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-400">Notwendige Cookies</p>
                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                      Diese Cookies sind für den Betrieb der Website erforderlich (Login, Warenkorb, Sicherheit). 
                      Sie können nicht deaktiviert werden.
                    </p>
                  </div>
                </div>
                <Switch checked={true} disabled className="ml-4" />
              </div>

              {/* Analytics Cookies (Matomo) */}
              <div className="flex items-start justify-between p-4 rounded-lg bg-muted/50 border">
                <div className="flex items-start gap-3">
                  <BarChart3 className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Statistik-Cookies (Matomo)</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren. 
                      Wir nutzen Matomo auf eigenen Servern in Frankfurt - deine Daten verlassen Deutschland nicht.
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={customConsent[COOKIE_CATEGORIES.analytics]} 
                  onCheckedChange={() => toggleCategory(COOKIE_CATEGORIES.analytics)}
                  className="ml-4"
                  data-testid="cookie-analytics-switch"
                />
              </div>

              <Button 
                onClick={handleCustomSave} 
                className="w-full mt-4"
                data-testid="cookie-save-settings"
              >
                Einstellungen speichern
              </Button>
            </div>
          )}

          {/* Links */}
          <div className="flex flex-wrap gap-4 pt-4 border-t text-xs text-muted-foreground">
            <Link to="/datenschutz" className="hover:text-primary hover:underline">
              Datenschutzerklärung
            </Link>
            <Link to="/impressum" className="hover:text-primary hover:underline">
              Impressum
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
